/**
 * Which own-ship vectors actually have a data source right now.
 *
 * Two of the four vectors come straight off the position fix (course/speed, and
 * heading in the simulator), and two need an outside source that may not be
 * wired in yet:
 *   - Heading needs a heading source. The simulator supplies one; a phone GPS on
 *     its own does not, and NMEA heading isn't plumbed in yet, so on a real fix
 *     there's nothing to draw a water track from.
 *   - Wind needs a wind source. The training world synthesises one; there's no
 *     real wind instrument input yet.
 *   - Tide needs tidal-stream data for where we are. We have it near the charted
 *     tidal diamonds; away from them the rate comes back zero and there's nothing
 *     to draw.
 *
 * Keeping this a pure function means the menu (to decide whether to show the
 * "connect a source" prompt) and the renderer (to decide whether to draw) agree,
 * and it can be unit-tested without any stores or a map.
 */
export type OwnVectorId = 'cogsog' | 'heading' | 'wind' | 'tide';

export interface VectorSourceInput {
  /** Do we have a position fix at all? */
  hasFix: boolean;
  /** 'sim' when the simulator is driving, 'device' for the real phone GPS. */
  sourceKind: 'sim' | 'device';
  /** Tidal-stream rate at the boat, knots (0 when there's no stream data here). */
  tideRateKn: number;
}

export type VectorAvailability = Record<OwnVectorId, boolean>;

export function vectorSourceAvailability(input: VectorSourceInput): VectorAvailability {
  const sim = input.sourceKind === 'sim';
  return {
    // Course/speed comes from the fix itself, so it's there whenever we have one.
    cogsog: input.hasFix,
    // Heading needs a heading source; only the simulator supplies one today.
    heading: input.hasFix && sim,
    // Wind needs a wind source; only the training world synthesises one today.
    wind: sim,
    // Tide needs stream data for our position; a real rate means we have it.
    tide: input.tideRateKn > 0,
  };
}
